import React, { FC, ReactElement, useCallback } from "react";
import { DropzoneOptions, FileRejection, useDropzone } from "react-dropzone";
import { Typography } from "@mui/material";
import { IconImage } from "@/components";
import { dropzon_upload_img } from "@/constants";
import {
  DropzoneContainer,
  DropzoneSmallText,
  DropzoneText,
  IconContainer,
} from "./styles.tsx";
import useSnackbar from "@/hooks/useSnackbar.ts";

interface DropzoneProps {
  setUploads?: React.Dispatch<React.SetStateAction<DropzoneTypes[]>>;
  onUpload?: (file: File) => void;
  accept?: string[];
  extensions?: string[];
  fileFormat?: string;
}

const Dropzone: FC<DropzoneProps & Omit<DropzoneOptions, "accept">> = ({
  setUploads,
  onUpload,
  accept = ["application/pdf"],
  extensions = [],
  fileFormat = "PDF",
  ...dropzoneProps
}): ReactElement => {
  const { show: toast } = useSnackbar();

  const onDrop = useCallback(
    (acceptedFiles: File[], fileRejections: FileRejection[]) => {
      if (fileRejections.length > 0) {
        toast(
          "Invalid file format. Please upload a correct format.",
          "",
          "error"
        );
        return;
      }
      if (acceptedFiles.length > 0) {
        const file = acceptedFiles[0];

        // explicitly validate through extensions
        if (extensions.length > 0) {
          const fileExtension = file.name.toLowerCase().split(".").pop();
          const isValid = extensions.includes(fileExtension ?? "");

          if (!isValid) {
            toast(
              "Invalid file format. Please upload a correct format.",
              "",
              "error"
            );

            return;
          }
        }

        onUpload?.(file);
        setUploads?.((prev) => [
          ...prev,
          {
            name: file.name,
            size: 34,
            progress: 35,
          },
        ]);
      }
    },
    [setUploads, onUpload, extensions, toast]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    ...dropzoneProps,
    onDrop,
    // skip the file type checking if the extensions are provided
    ...(extensions.length === 0 && {
      accept: accept.reduce((acc, type) => {
        acc[type] = [];
        return acc;
      }, {} as Record<string, []>),
    }),
  });

  return (
    <section>
      <DropzoneContainer
        {...getRootProps()}
        className={isDragActive ? "active" : ""}
      >
        <input {...getInputProps()} />
        {isDragActive ? (
          <DropzoneText>Drop the files here ...</DropzoneText>
        ) : (
          <>
            <IconContainer>
              <IconImage src={dropzon_upload_img} size="auto" />
            </IconContainer>
            <Typography color="#000" marginBottom={"5px"} variant="body1">
              Drag & drop files or{" "}
              <span
                style={{
                  color: "#0B494B",
                  cursor: "pointer",
                  textDecoration: "underline",
                  textUnderlineOffset: "3px",
                }}
              >
                browse files
              </span>
            </Typography>

            <DropzoneSmallText>
              {`${fileFormat}${
                dropzoneProps.maxSize
                  ? " - Max file size " +
                    dropzoneProps.maxSize / 1024 / 1024 +
                    "MB"
                  : ""
              }`}
            </DropzoneSmallText>
          </>
        )}
      </DropzoneContainer>
    </section>
  );
};

export default Dropzone;
